// Phase 1E -- Work: one queue for everything Martian prepared.
// "Ready for review" is the default and the only emphasised state; approved
// and declined work is one tab away (Hick: 3 views, not 6 state tabs).
// Every item answers: why Martian created it, which decision it belongs to,
// what business context it was checked against, and what Martian needs.
// Review -> Approve (primary) / Edit / Decline. Creative joins Work as "Create".
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, PencilSimple, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { preparedWorkAPI } from '@/lib/api';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import CreativeIntelligencePage from '@/pages/CreativeIntelligencePage';
import { useFounderWorkspace, useFounderPath } from '../FounderWorkspace';
import { approvalMessage, measurementStage } from '../measurementStage';
import { Chip, EmptyState, Eyebrow, Loading, PageHeader, PrimaryButton, SecondaryButton, TextButton, formatDate } from '../ui';
import { GLIMState } from '@/brand';

const OPEN = ['prepared', 'reviewing'];
// The backend has no "failed" status: a draft whose generation failed stays
// prepared/reviewing with no prepared_output (prepared_work.py). It has
// nothing to review or approve, so it is never counted as ready.
const isFailedDraft = (w) => OPEN.includes(w.status) && !w.prepared_output;
const isReviewable = (w) => OPEN.includes(w.status) && !!w.prepared_output;
const VIEWS = [
  { key: 'ready', label: 'Ready for review' },
  { key: 'approved', label: 'Approved' },
  { key: 'all', label: 'All work' },
];
const STATUS_CHIP = {
  prepared: { label: 'Ready for review', tone: 'accent' },
  reviewing: { label: 'Ready for review', tone: 'accent' },
  approved: { label: 'Approved', tone: 'good' },
  dismissed: { label: 'Declined', tone: 'neutral' },
};
const WORK_TYPE = { content_brief: 'Content brief', faq: 'FAQ', comparison_page: 'Comparison page', social_post: 'Social post', campaign_brief: 'Campaign brief' };
const FIELD_LABEL = {
  suggested_headline: 'Headline', objective: 'Objective', recommended_angle: 'Angle',
  what_to_create: 'What to create', key_points: 'Key points', success_signal: 'How we will know it worked',
};
const FIELD_ORDER = ['suggested_headline', 'what_to_create', 'key_points', 'objective', 'recommended_angle', 'success_signal'];

function decisionFor(summary, workId) {
  return (summary?.decisions || []).find((d) => d.prepared_work?.id === workId || (d.other_work || []).some((w) => w.id === workId)) || null;
}

function Tabs({ view, setView, counts }) {
  return (
    <div role="tablist" aria-label="Work" className="flex gap-1 border-b border-[var(--border)] mb-2 overflow-x-auto">
      {VIEWS.map((v) => (
        <button key={v.key} role="tab" type="button" aria-selected={view === v.key} onClick={() => setView(v.key)} data-testid={`work-tab-${v.key}`}
          className={`h-10 px-3 -mb-px border-b-2 text-[14px] whitespace-nowrap focus-visible:outline-none focus-visible:[box-shadow:var(--shadow-focus-ring)] ${view === v.key ? 'border-[var(--accent)] text-[var(--text-primary)] font-semibold' : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}>
          {v.label}{counts[v.key] !== undefined && <span className="ml-1.5 text-[12px] text-[var(--text-tertiary)] tabular-nums">{counts[v.key]}</span>}
        </button>
      ))}
    </div>
  );
}

const FAILED_CHIP = { label: "Couldn't finish draft", tone: 'warn' };

function ReviewSheet({ work, decision, open, onOpenChange, onChanged, workspaceId, fp }) {
  const navigate = useNavigate();
  const { openAsk, summary } = useFounderWorkspace();
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  useEffect(() => { setEditing(false); setDraft({}); }, [work?.id]);
  if (!work) return null;
  const out = work.prepared_output || {};
  const isOpen = OPEN.includes(work.status);
  const grounding = work.grounding || {};
  const groundedIn = (grounding.context_fields_used || []).map((r) => ({ field: r.field.replace(/_/g, ' '), value: Array.isArray(r.value) ? r.value.join(', ') : r.value }));

  const act = async (fn, success) => {
    setBusy(true);
    try {
      const updated = await fn();
      if (typeof success === 'string') toast.success(success); else toast.success(success.text, success.options);
      onChanged(updated);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save that — please try again"); } finally { setBusy(false); }
  };
  // Approval alone never starts measuring: weekly checks also need the
  // questions Martian watches to be approved. Say which applies, from the
  // monitoring state the backend already reported.
  const approvedNote = () => {
    const msg = approvalMessage(summary?.monitoring);
    return msg.needsQuestions
      ? { text: msg.text, options: { action: { label: 'Approve questions', onClick: () => navigate(fp('/business/watchlist')) } } }
      : { text: msg.text };
  };
  const approve = () => act(async () => {
    if (work.status === 'prepared') await preparedWorkAPI.updateStatus(workspaceId, work.id, 'reviewing');
    return (await preparedWorkAPI.updateStatus(workspaceId, work.id, 'approved')).data;
  }, approvedNote());
  const decline = () => act(async () => (await preparedWorkAPI.updateStatus(workspaceId, work.id, 'dismissed')).data, 'Declined');
  const retry = () => act(async () => (await preparedWorkAPI.retryGeneration(workspaceId, work.id)).data, 'Brief generated');
  const saveEdit = () => act(async () => {
    const fields = {};
    Object.entries(draft).forEach(([k, v]) => { fields[k] = k === 'key_points' ? v.split('\n').map((s) => s.trim()).filter(Boolean) : v; });
    const res = (await preparedWorkAPI.editOutput(workspaceId, work.id, fields)).data;
    setEditing(false);
    return res;
  }, 'Your edits are saved');

  const startEdit = () => {
    const d = {};
    FIELD_ORDER.forEach((k) => { if (out[k] !== undefined) d[k] = Array.isArray(out[k]) ? out[k].join('\n') : out[k]; });
    setDraft(d);
    setEditing(true);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[640px] h-full p-0 flex flex-col bg-[var(--surface)] [&>button]:hidden" data-testid="work-review-sheet">
        <div className="flex items-start justify-between gap-3 px-6 pt-6 pb-4 border-b border-[var(--border)]">
          <SheetHeader className="text-left space-y-1 min-w-0">
            <SheetDescription className="text-[12.5px] text-[var(--text-tertiary)]">{WORK_TYPE[work.type] || 'Prepared work'} · prepared {formatDate(work.created_at)}</SheetDescription>
            <SheetTitle className="font-display text-[20px] leading-snug text-balance">{out.suggested_headline || work.title}</SheetTitle>
          </SheetHeader>
          <button type="button" onClick={() => onOpenChange(false)} aria-label="Close" className="p-2 -mr-2 rounded-[var(--radius-button)] text-[var(--text-secondary)] hover:bg-[var(--canvas-tertiary)]"><X size={18} /></button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-6">
          {/* Why Martian created this / which decision */}
          <div>
            <Eyebrow>Why Martian created this</Eyebrow>
            <p className="mt-1.5 text-[14.5px] leading-relaxed">{decision?.found || work.why_now}</p>
            {decision && <div className="mt-1.5"><TextButton onClick={() => { onOpenChange(false); navigate(fp(`/decisions/${encodeURIComponent(decision.id)}`)); }}>Open the decision</TextButton></div>}
          </div>

          {/* The brief */}
          {work.prepared_output ? (
            <div className="space-y-5" data-testid="work-review-brief">
              {FIELD_ORDER.filter((k) => out[k] !== undefined).map((k) => (
                <div key={k}>
                  <Eyebrow>{FIELD_LABEL[k]}</Eyebrow>
                  {editing ? (
                    <textarea value={draft[k] ?? ''} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })} rows={k === 'key_points' ? 5 : k === 'suggested_headline' ? 2 : 3}
                      aria-label={FIELD_LABEL[k]} data-testid={`work-edit-${k}`}
                      className="mt-1.5 w-full rounded-[var(--radius-input)] border border-[var(--border)] bg-[var(--surface-sunken)] px-3 py-2 text-[14px] leading-relaxed focus-visible:outline-none focus-visible:[box-shadow:var(--shadow-focus-ring)]" />
                  ) : Array.isArray(out[k]) ? (
                    <ul className="mt-1.5 space-y-1.5 list-disc pl-5 text-[14.5px] leading-relaxed">{out[k].map((p, i) => <li key={i}>{p}</li>)}</ul>
                  ) : (
                    <p className="mt-1.5 text-[14.5px] leading-relaxed">{out[k]}</p>
                  )}
                </div>
              ))}
              {editing && <p className="text-[12.5px] text-[var(--text-tertiary)]">One key point per line.</p>}
              {work.edited_by_founder && !editing && <p className="text-[12.5px] text-[var(--text-tertiary)]">You edited: {(work.edited_fields || []).map((f) => FIELD_LABEL[f] || f).join(', ')}.</p>}
            </div>
          ) : (
            <div className="rounded-[var(--radius-card)] bg-[var(--surface-sunken)] p-4" data-testid="work-review-failed">
              <p className="text-[14px]">Martian couldn't finish drafting this brief.</p>
              <p className="mt-1 text-[13px] text-[var(--text-secondary)]">There is nothing to review or approve until it has been drafted.</p>
              {isOpen && (
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <SecondaryButton onClick={retry} disabled={busy} testId="work-retry">Try again</SecondaryButton>
                  <TextButton onClick={decline} disabled={busy} tone="danger" testId="work-failed-decline">Decline</TextButton>
                </div>
              )}
            </div>
          )}

          {/* Grounded in */}
          {grounding.status === 'passed' && (
            <div data-testid="work-review-grounding">
              <p className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[var(--martian-mint-ink,#3F7A64)]"><CheckCircle size={15} weight="fill" /> Checked against your business</p>
              <dl className="mt-2 space-y-1 text-[13px]">
                {groundedIn.map((g) => <div key={g.field} className="flex gap-2"><dt className="w-36 shrink-0 text-[var(--text-tertiary)] capitalize">{g.field}</dt><dd className="min-w-0 text-[var(--text-secondary)] line-clamp-2">{g.value}</dd></div>)}
              </dl>
            </div>
          )}
          {work.groundedness_flags?.length > 0 && (
            <p className="text-[13px] text-[var(--martian-yellow-ink,#7A5F12)]">Check before publishing: this brief mentions a number or timeframe Martian couldn't confirm from its evidence.</p>
          )}
        </div>

        {/* What Martian needs from you */}
        {isOpen && work.prepared_output && (
          <div className="px-6 py-4 border-t border-[var(--border)] flex flex-wrap items-center gap-3" data-testid="work-review-actions">
            {editing ? (
              <>
                <PrimaryButton onClick={saveEdit} disabled={busy} testId="work-save-edit">Save edits</PrimaryButton>
                <TextButton onClick={() => setEditing(false)}>Cancel</TextButton>
              </>
            ) : (
              <>
                <GLIMState state="approval_required" size={44} showDescription className="mr-2" />
                <PrimaryButton onClick={approve} disabled={busy} testId="work-approve">Approve</PrimaryButton>
                <SecondaryButton onClick={startEdit} disabled={busy} testId="work-edit"><PencilSimple size={15} /> Edit</SecondaryButton>
                <TextButton onClick={() => openAsk({ domain: work.agent_type || 'ai_search', entityType: 'prepared_work', entityId: work.id, entityLabel: work.title, surface: 'work' })}>Ask for changes</TextButton>
                <span className="flex-1" />
                <TextButton onClick={decline} disabled={busy} tone="danger" testId="work-decline">Decline</TextButton>
              </>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default function WorkPage({ view: initialView }) {
  const { workspaceId, summary, refreshSummary, loadingClients } = useFounderWorkspace();
  const fp = useFounderPath();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState(null);
  const [view, setView] = useState('ready');

  const load = useCallback(() => {
    if (!workspaceId) return;
    preparedWorkAPI.list(workspaceId).then((res) => setItems(res.data?.items || [])).catch(() => setItems([]));
  }, [workspaceId]);
  useEffect(() => { setItems(null); load(); }, [load]);

  const counts = useMemo(() => ({
    ready: (items || []).filter(isReviewable).length,
    approved: (items || []).filter((w) => w.status === 'approved').length,
  }), [items]);
  const failedCount = (items || []).filter(isFailedDraft).length;

  const selectedId = params.get('item');
  const selected = (items || []).find((w) => w.id === selectedId) || null;
  const closeReview = () => { params.delete('item'); setParams(params, { replace: true }); };
  const onChanged = (updated) => {
    if (updated?.id) setItems((prev) => (prev || []).map((w) => (w.id === updated.id ? { ...w, ...updated } : w)));
    refreshSummary();
    if (updated && !OPEN.includes(updated.status)) closeReview();
  };

  if (initialView === 'create') {
    return (
      <div data-testid="work-create-page">
        <PageHeader eyebrow="Work" title="Create" summary="Turn a brief into an ad image for your business. Martian uses what it knows about your business in every image prompt." secondary={<SecondaryButton to={fp('/work')}>Back to work</SecondaryButton>} />
        <CreativeIntelligencePage embedded workspaceId={workspaceId} />
      </div>
    );
  }
  if (loadingClients || (workspaceId && items === null)) return <Loading />;

  const shown = (items || []).filter((w) => (view === 'ready' ? isReviewable(w) : view === 'approved' ? w.status === 'approved' : true));

  return (
    <div data-testid="work-page">
      <PageHeader
        eyebrow="Work"
        title={counts.ready ? `${counts.ready} ready for your review` : 'Nothing waiting for review'}
        summary={`Everything Martian prepared for you. Nothing is published or sent until you approve it.${failedCount ? ` ${failedCount} draft${failedCount === 1 ? '' : 's'} couldn't be finished — see All work to try again.` : ''}`}
        secondary={<SecondaryButton to={fp('/work/create')} testId="work-create">Create an image</SecondaryButton>}
      />
      <Tabs view={view} setView={setView} counts={counts} />
      {shown.length === 0 ? (
        <EmptyState title={view === 'ready' ? 'Nothing waiting for you' : 'Nothing here yet'}
          body={view === 'ready' ? 'When Martian prepares work for a decision, it appears here for your review.' : undefined} />
      ) : (
        <ul className="divide-y divide-[var(--border)]">
          {shown.map((w, idx) => {
            const d = decisionFor(summary, w.id);
            const failed = isFailedDraft(w);
            const chip = failed ? FAILED_CHIP : (STATUS_CHIP[w.status] || STATUS_CHIP.prepared);
            const stage = w.status === 'approved' && d ? measurementStage(d.measurement) : null;
            const out = w.prepared_output || {};
            return (
              <li key={w.id} className="py-5 flex flex-col sm:flex-row sm:items-start gap-4" data-testid="work-row">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-1.5">
                    <Chip tone={chip.tone}>{chip.label}</Chip>
                    <span className="text-[12.5px] text-[var(--text-tertiary)]">{WORK_TYPE[w.type] || 'Prepared work'} · {formatDate(w.created_at)}</span>
                  </div>
                  <p className="text-[16px] font-semibold leading-snug text-balance">{out.suggested_headline || w.title}</p>
                  {d && <p className="mt-1 text-[13.5px] text-[var(--text-secondary)]">For: {d.found}</p>}
                  {w.grounding?.status === 'passed' && <p className="mt-1 inline-flex items-center gap-1 text-[12.5px] text-[var(--martian-mint-ink,#3F7A64)]"><CheckCircle size={13} weight="fill" /> Checked against your business</p>}
                  {failed && <p className="mt-1 text-[12.5px] text-[var(--text-secondary)]" data-testid="work-row-failed">Martian couldn't finish this draft. Open it to try again.</p>}
                  {stage?.key === 'awaiting_questions' && (
                    <p className="mt-1 text-[12.5px] text-[var(--text-secondary)]" data-testid="work-row-measurement">
                      Not measured yet: approve the questions Martian watches.{' '}
                      <TextButton to={fp(stage.action.to)} tone="accent">{stage.action.label}</TextButton>
                    </p>
                  )}
                </div>
                <div className="shrink-0">
                  {/* Von Restorff: only the first item waiting for review gets the filled button. */}
                  {isReviewable(w) && idx === 0
                    ? <PrimaryButton onClick={() => setParams({ item: w.id })} testId="work-review">Review</PrimaryButton>
                    : <SecondaryButton onClick={() => setParams({ item: w.id })} testId="work-open">{isReviewable(w) ? 'Review' : 'Open'}</SecondaryButton>}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <ReviewSheet work={selected} decision={selected ? decisionFor(summary, selected.id) : null} open={!!selected}
        onOpenChange={(o) => { if (!o) closeReview(); }} onChanged={onChanged} workspaceId={workspaceId} fp={fp} navigate={navigate} />
    </div>
  );
}
